import { debounce } from "@mui/material";
import { AxiosError } from "axios";
import React, { Dispatch, SetStateAction, createContext, useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { HandledRequestError } from "../Components/ErrorHandling/ErrorHandler";
import { EnhancedCenter } from "../Models/Center";
import { Customer, EnhancedCustomer, UnverifiedCustomer } from "../Models/Customer";
import { EnhancedCustomerNote } from "../Models/CustomerNote";
import { DisqualifyingReason } from "../Models/DisqualifyingReason";
import { GemError } from "../Models/GemError";
import { Lead, LeadResult } from "../Models/Lead";
import { User } from "../Models/User";
import { DateTime } from "../Types/DateTime";
import { DynamicContentQuery } from "../Types/DynamicContentView";
import { PhoneNumberCache } from "../Types/PhoneNumberCache";
import { PhoneNumberErrors } from "../Types/PhoneNumberErrors";
import { SaveCustomer, Session, SessionCache } from "../Types/Session";
import { Step } from "../Types/Step";
import { DisqualReason, LeadExternalSystem } from "../Utils/Enums";
import { buildError, zipCodeIsValid } from "../Utils/Helpers";
import { loadFromLocal as utilLoadFromLocal, saveToLocal as utilSaveToLocal } from "../Utils/LocalStorage";
import OpsMappingUtility from "../Utils/OpsMappingUtility";
import EnvironmentConfiguration from "../V2/Constants/EnvironmentConfiguration";
import { IMedicalFormConclusions } from "../V2/Playbook/Medical/Types/IMedicalFormConclusions";

export interface SessionContextProps {
  apptErrorCenters: string[];
  appointment?: { id: string; businessUnit: string; serviceSubCategory: string };
  bookingDisquals: string[];
  customer: EnhancedCustomer;
  duplicateCustomers: Customer[];
  dynamicContentQuery?: DynamicContentQuery;
  ignoreNonCan: boolean;
  /** A flag which indicates if the lead for this session was a manually created outbound. */
  isLeadManualOutbound: boolean;
  /** A flag which indicates if the session has experienced any steps other than Intro and Disposition. */
  isSessionShortCircuited: boolean;
  isSavingCustomer: boolean;
  isSearchComplete: boolean;
  /** A flag which indicates that the customer state in the back-end fulfills all constraints to allow booking. */
  isCustomerValidForOpsBooking: boolean;
  lead: Lead;
  leadDisqualifiers: DisqualifyingReason[];
  leadResult: LeadResult | null;
  /** Miscellaneous data resolved from answers to the medical form. */
  medicalConclusions: IMedicalFormConclusions;
  note: EnhancedCustomerNote;
  overrideEmail: string | null;
  phoneNumberCache: PhoneNumberCache;
  previewCustomer?: EnhancedCustomer;
  previousSession?: Session;
  phoneNumberErrors: PhoneNumberErrors;
  requestErrors: AxiosError<GemError>[];
  saveCustomer?: { customer: EnhancedCustomer; override: boolean; forceUpdate?: true };
  selectedCustomer?: EnhancedCustomer;
  showBookingWizard: boolean;
  showDuplicateModal: boolean;
  step: Step;
  unverifiedCustomer: UnverifiedCustomer;

  setApptErrorCenters: Dispatch<SetStateAction<string[]>>;
  setAppointment: Dispatch<
    SetStateAction<{ id: string; businessUnit: string; serviceSubCategory: string } | undefined>
  >;
  setCustomer: Dispatch<SetStateAction<EnhancedCustomer>>;
  setCustomerInfo: (customerUpdate: Partial<EnhancedCustomer>) => void;
  setDuplicateCustomers: Dispatch<SetStateAction<Customer[]>>;
  setDynamicContentQuery: (query: DynamicContentQuery) => void;
  setIsCustomerValidForOpsBooking: Dispatch<boolean>;
  setIgnoreNonCan: Dispatch<SetStateAction<boolean>>;
  setIsSessionShortCircuited: Dispatch<SetStateAction<boolean>>;
  setIsSavingCustomer: Dispatch<SetStateAction<boolean>>;
  setIsSearchComplete: Dispatch<SetStateAction<boolean>>;
  setManualLead?: (lead: Lead) => void;
  setMedicalConclusions: Dispatch<SetStateAction<IMedicalFormConclusions>>;
  setLeadResult: Dispatch<SetStateAction<LeadResult | null>>;
  setPreviewCustomer: Dispatch<SetStateAction<EnhancedCustomer | undefined>>;
  setNote: Dispatch<SetStateAction<EnhancedCustomerNote>>;
  setOverrideEmail: Dispatch<SetStateAction<string | null>>;
  setPhoneNumberCache: Dispatch<SetStateAction<PhoneNumberCache>>;
  setPhoneNumberErrors: Dispatch<SetStateAction<PhoneNumberErrors>>;
  setSaveCustomer: (update: { customer: EnhancedCustomer; override: boolean; forceUpdate?: true }) => void;
  setSelectedCustomer: (oldRibbonCustomer?: Customer) => void;
  setShowBookingWizard: Dispatch<SetStateAction<boolean>>;
  setShowDuplicateModal: Dispatch<SetStateAction<boolean>>;
  setStep: Dispatch<SetStateAction<Step>>;
  setUnverifiedCustomer: Dispatch<SetStateAction<UnverifiedCustomer>>;
  setOverrideContact: Dispatch<boolean>;

  helpers: {
    handledError: HandledRequestError | undefined;
    addRequestError: (error: AxiosError<GemError>) => void;
    /** Callback to end the session and send the user to call-waiting. */
    endSession: () => void;
    /** Callback to load a record from the cache for this session. */
    loadFromLocal<T>(key?: string): T | undefined;
    qualify: (reasonEnum: DisqualReason, isQualified: boolean) => void;
    saveToLocal: (obj: unknown, key?: string) => void;
    setHandledError: (error: HandledRequestError) => void;
  };
}

export const SessionContext = createContext({} as SessionContextProps);

interface Props {
  centers: EnhancedCenter[];
  disqualifyingReasons: DisqualifyingReason[];
  isActive: boolean;
  lead: Lead;
  user: User;
  endSession: () => void;
  setManualLead?: (lead: Lead) => void;
  setNavCustomer: (customer: EnhancedCustomer) => void;
  setNavErrors: (errors: unknown[]) => void;
  children: React.ReactNode;
}
const SessionContextProvider: React.FC<Props> = ({
  centers,
  disqualifyingReasons,
  isActive,
  lead,
  user,
  endSession,
  setManualLead,
  setNavCustomer,
  setNavErrors,
  children
}: Props) => {
  const saveToLocal = useCallback(
    (obj: unknown, key?: string): void => utilSaveToLocal(obj, `${lead.id || "manualLead"}${key ? `_${key}` : ""}`),
    [lead.id]
  );
  const loadFromLocal = useCallback(
    <T,>(key?: string): T | undefined => utilLoadFromLocal<T>(`${lead.id || "manualLead"}${key ? `_${key}` : ""}`),
    [lead.id]
  );

  const [initSession] = useState<Session>(() => {
    try {
      return new Session(lead, centers, Boolean(setManualLead), disqualifyingReasons, loadFromLocal<SessionCache>());
    } catch {
      // when we utterly fail to load the cache clear the cache and reload
      localStorage.clear();
      window.location.reload();
    }
    throw "Failed to load from the cache. Reloading.";
  });
  const [apptErrorCenters, setApptErrorCenters] = useState(() => initSession.apptErrorCenters);
  const [appointment, setAppointment] = useState(() => initSession.appointment);
  const [bookingDisquals, setBookingDisqualifiers] = useState<string[]>([]);
  const [customer, setCustomer] = useState(() => initSession.customer);
  const [duplicateCustomers, setDuplicateCustomers] = useState(() => initSession.duplicateCustomers);
  const [dynamicContentQuery, setDynamicContentQuery] = useState<DynamicContentQuery>();
  const [ignoreNonCan, setIgnoreNonCan] = useState(false);
  const [isCustomerValidForOpsBooking, setIsCustomerValidForOpsBooking] = useState(
    () => initSession.isCustomerValidForOpsBooking
  );
  const [isSessionShortCircuited, setIsSessionShortCircuited] = useState(() => initSession.isSessionShortCircuited);
  const [isSavingCustomer, setIsSavingCustomer] = useState(false);
  const [isSearchComplete, setIsSearchComplete] = useState(() => initSession.isSearchComplete);
  const [leadDisqualifiers, setLeadDisqualifiers] = useState(() => initSession.leadDisqualifiers);
  const [leadResult, setLeadResult] = useState(() => initSession.leadResult);
  const [medicalConclusions, setMedicalConclusions] = useState(() => initSession.medicalConclusions);
  const [note, setNote] = useState(() => initSession.note);
  const [overrideEmail, setOverrideEmail] = useState(() => lead.email);
  const [previewCustomer, setPreviewCustomer] = useState<EnhancedCustomer>();
  const [phoneNumberCache, setPhoneNumberCache] = useState<PhoneNumberCache>({});
  const [phoneNumberErrors, setPhoneNumberErrors] = useState<PhoneNumberErrors>({});
  const [requestErrors, setRequestErrors] = useState(() => initSession.requestErrors);
  const [saveCustomer, setSaveCustomer] = useState<SaveCustomer>();
  const [selectedCustomer, setSelectedCustomer] = useState(() => initSession.selectedCustomer);
  const [showBookingWizard, setShowBookingWizard] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [step, setStep] = useState<Step>(() => initSession.step);
  const [unverifiedCustomer, setUnverifiedCustomer] = useState(() => initSession.unverifiedCustomer);
  const [overrideContact, setOverrideContact] = useState<boolean>();

  //  <----------------------- SET/SAVE CALLBACKS --------------------->

  const setCustomerInfo: SessionContextProps["setCustomerInfo"] = useCallback(
    u => setCustomer(c => ({ ...c, ...u })),
    []
  );
  const leadRef = useRef(lead);
  useEffect(() => {
    leadRef.current = lead;
  }, [lead]);
  const previewCustomerRef = useRef(previewCustomer);
  useEffect(() => {
    previewCustomerRef.current = previewCustomer;
  }, [previewCustomer]);
  const overrideEmailRef = useRef(lead.email);
  useEffect(() => {
    overrideEmailRef.current = overrideEmail;
  }, [overrideEmail]);

  const saveSelectedCustomer = useCallback(
    (oldRibbonCustomer?: Customer) => {
      // if we have selected a customer from the search step or duplicate modal use it
      let loadedCustomer: EnhancedCustomer | undefined = undefined;
      if (previewCustomerRef.current) {
        setSelectedCustomer({ ...previewCustomerRef.current });
        loadedCustomer = { ...previewCustomerRef.current };
        setPreviewCustomer(undefined);
      }
      // else if we have a lead outbound customer, use it
      else if (leadRef.current.outbound) {
        setSelectedCustomer(
          new EnhancedCustomer({
            ...leadRef.current.outbound.customer,
            center: centers.find(c => c.id === leadRef.current.outbound?.customer.centerId)
          })
        );
        loadedCustomer = new EnhancedCustomer({
          ...leadRef.current.outbound.customer,
          center: centers.find(c => c.id === leadRef.current.outbound?.customer.centerId),
          opportunity: { ...leadRef.current.outbound.opportunity }
        });
      } else setSelectedCustomer(undefined);
      // if we have a loaded customer with a center id, add the center
      if (loadedCustomer?.centerId)
        loadedCustomer.center = centers.find(c => c.id === (loadedCustomer?.centerId as string));

      // start our ribbon customer, either from the loaded customer if any, or a new customer
      const ribbonCustomer: EnhancedCustomer = loadedCustomer ? { ...loadedCustomer } : new EnhancedCustomer();
      // IF the lead has values for select props, use them
      // TODO: This  is brittle, and the lead information is lost when the lead external system does not exactly match the given types
      if (
        (leadRef.current.externalSystem === LeadExternalSystem.WebForm ||
          leadRef.current.externalSystem === LeadExternalSystem.InContactChat) &&
        !overrideContact
      ) {
        if (leadRef.current.phoneNumber) ribbonCustomer.phoneNumber = leadRef.current.phoneNumber;
        if (leadRef.current.email) ribbonCustomer.email = leadRef.current.email;
        if (leadRef.current.firstName) ribbonCustomer.firstName = leadRef.current.firstName;
        if (leadRef.current.lastName) ribbonCustomer.lastName = leadRef.current.lastName;
      }

      if (leadRef.current.externalSystem === LeadExternalSystem.Email && overrideEmailRef.current)
        ribbonCustomer.email = overrideEmailRef.current;
      if (leadRef.current.centerId) {
        ribbonCustomer.centerId = leadRef.current.centerId;
        ribbonCustomer.center = centers.find(({ id }) => id === leadRef.current.centerId);
      }
      if (leadRef.current.zipCode) ribbonCustomer.zipCode = leadRef.current.zipCode;
      if (!ribbonCustomer.opportunity?.id)
        ribbonCustomer.opportunity = {
          campaign: leadRef.current.campaign,
          centerId: ribbonCustomer.centerId || undefined,
          createdByUserId: user.id
        };
      // if we have been passed an existing ribbon customer state
      if (oldRibbonCustomer) {
        oldRibbonCustomer.id = null;
        oldRibbonCustomer.externalId = null;
        oldRibbonCustomer.code = null;
        oldRibbonCustomer.opportunity = null;
        // for each prop with a TRUTHY value (NOT ""/null/undefined), use it
        Object.assign(ribbonCustomer, Object.fromEntries(Object.entries(oldRibbonCustomer).filter(([, v]) => !!v)));
      }
      setIsCustomerValidForOpsBooking(OpsMappingUtility.isCustomerValidForBooking(ribbonCustomer));
      setCustomer(ribbonCustomer);
      setUnverifiedCustomer(new UnverifiedCustomer(ribbonCustomer));
    },
    [centers, user, overrideContact]
  );

  // when session variables change, save to local
  useEffect(() => {
    saveToLocal(
      new SessionCache({
        apptErrorCenters,
        appointment,
        customer,
        duplicateCustomers,
        isCustomerValidForOpsBooking,
        isSessionShortCircuited,
        isSearchComplete,
        leadDisqualifiers,
        leadResult,
        medicalConclusions,
        note,
        phoneNumberCache,
        requestErrors,
        selectedCustomer,
        step,
        unverifiedCustomer
      })
    );
  }, [
    // do not trigger on unvalidated state changes
    apptErrorCenters,
    appointment,
    customer,
    duplicateCustomers,
    isCustomerValidForOpsBooking,
    isSessionShortCircuited,
    isSearchComplete,
    leadDisqualifiers,
    leadResult,
    medicalConclusions,
    note,
    phoneNumberCache,
    requestErrors,
    selectedCustomer,
    step,
    saveToLocal
  ]);

  // <----------------------- HELPER FUNCTIONS --------------------->
  const debouncedToast = useCallback(
    debounce((qualified: boolean) => {
      if (qualified) {
        toast.success("The guest is now qualified.", { toastId: "guestQualified" });
        toast.dismiss("guestDisqualified");
      } else {
        toast.info("The guest is now disqualified.", { toastId: "guestDisqualified" });
        toast.dismiss("guestQualified");
      }
    }, 1000),
    []
  );
  const qualify = useCallback(
    (reasonEnum: DisqualReason, isQualified: boolean) => {
      const reason = disqualifyingReasons.find(dr => dr.name === reasonEnum) as DisqualifyingReason;
      if (!isQualified)
        setLeadDisqualifiers(ld => {
          if (!ld.some(ld => ld.id === reason.id)) {
            if (!ld.length) debouncedToast(false);
            return [...ld, reason];
          }
          return ld;
        });
      else
        setLeadDisqualifiers(ld => {
          if (ld.some(ld => ld.id === reason.id)) {
            if (ld.length === 1) debouncedToast(true);
            return ld.filter(ld => ld.id !== reason.id);
          }
          return ld;
        });
    },
    [disqualifyingReasons, debouncedToast]
  );
  const addRequestError = useCallback((error: AxiosError<GemError>) => {
    setRequestErrors(errors => [...errors, error]);
  }, []);
  const [handledError, setHandledError] = useState<HandledRequestError>();

  // <---------------------- SIDE EFFECTS ---------------------->
  // on bmi/center/dob change, update disqualifiers
  useEffect(() => {
    if (customer.bmi)
      qualify(
        DisqualReason.BMI,
        customer.bmi > EnvironmentConfiguration.bmi.lowerLimit && customer.bmi < EnvironmentConfiguration.bmi.upperLimit
      );
    if (customer.dateOfBirth)
      qualify(DisqualReason.Age, DateTime.now().diff(DateTime.fromISO(customer.dateOfBirth), "years").years >= 18);
  }, [customer.bmi, customer.center, customer.dateOfBirth]);

  // on center change, silently update opportunity and note
  useEffect(() => {
    if (!customer.centerId) return;
    setNote(n => {
      n.centerId = customer.centerId as string;
      return n;
    });
    setCustomer(c => {
      if (c.opportunity) c.opportunity.centerId = customer.centerId || undefined;
      return c;
    });
  }, [customer.centerId]);

  // on height/weight change, update bmi
  useEffect(() => {
    let bmi = null;
    if (customer.height?.feet && customer.weight) {
      const heightInInches = customer.height.feet * 12 + Number(customer.height.inches);
      bmi = Number(((customer.weight * 703) / (heightInInches * heightInInches)).toFixed(2));
    }
    setCustomerInfo({ bmi });
  }, [customer.height, customer.weight, setCustomerInfo]);

  // on firstN/lastN/dob/center/height/weight/improveAreas/disqualifiers change, update disqualifiers text
  // new OPS requirement: phone number & distance to Center.
  useEffect(() => {
    const disqualifiers: string[] = [];
    if (!customer.phoneNumber) disqualifiers.push("Missing Phone Number");
    if (!customer.firstName || !customer.lastName) disqualifiers.push("Incomplete Guest Name");
    if (!customer.dateOfBirth) disqualifiers.push("Missing Birth Date");
    if (!customer.center?.id) disqualifiers.push("No Center Selected");
    if (!customer.height) disqualifiers.push("Missing Height");
    if (!customer.weight) disqualifiers.push("Missing Weight");
    if (customer.zipCode && !zipCodeIsValid(customer.zipCode)) disqualifiers.push("Bad ZipCode");
    if (!Object.values(customer.improvementAreas).includes(true)) disqualifiers.push("Missing Improvement Area");
    const disqualString = leadDisqualifiers.reduce((prev, { name }) => (prev ? `${prev}, ${name}` : name), "");
    if (disqualString) disqualifiers.push(`NonCan: ${disqualString}`);
    if (!customer.distanceToCenter) disqualifiers.push("Missing Distance to Center");
    setBookingDisqualifiers(disqualifiers);
  }, [
    customer.phoneNumber,
    customer.firstName,
    customer.lastName,
    customer.dateOfBirth,
    customer.center,
    customer.height,
    customer.weight,
    customer.improvementAreas,
    customer.zipCode,
    customer.distanceToCenter,
    customer.centerId,
    leadDisqualifiers
  ]);

  // on mount, if no selected customer and lead has outbound, set it as selected customer
  useEffect(() => {
    if (lead.outbound && !selectedCustomer) saveSelectedCustomer();
  }, []);

  // if session is active, live update nav customer
  useEffect(() => {
    if (isActive) setNavCustomer(customer);
  }, [isActive, customer, setNavCustomer]);
  // update nav errors
  useEffect(() => setNavErrors(requestErrors.map(e => buildError(e))), [requestErrors, setNavErrors]);

  return (
    <SessionContext.Provider
      value={{
        apptErrorCenters,
        appointment,
        bookingDisquals,
        customer,
        duplicateCustomers,
        dynamicContentQuery,
        ignoreNonCan,
        isCustomerValidForOpsBooking,
        isLeadManualOutbound: Boolean(setManualLead),
        isSessionShortCircuited,
        isSavingCustomer,
        isSearchComplete,
        lead,
        leadDisqualifiers,
        leadResult,
        medicalConclusions,
        note,
        overrideEmail,
        phoneNumberCache,
        phoneNumberErrors,
        previewCustomer,
        requestErrors,
        saveCustomer,
        selectedCustomer,
        showBookingWizard,
        showDuplicateModal,
        step,
        unverifiedCustomer,

        setApptErrorCenters,
        setAppointment,
        setCustomer,
        setCustomerInfo,
        setDuplicateCustomers,
        setDynamicContentQuery,
        setIgnoreNonCan,
        setIsCustomerValidForOpsBooking,
        setIsSessionShortCircuited,
        setIsSavingCustomer,
        setIsSearchComplete,
        setManualLead,
        setLeadResult,
        setMedicalConclusions,
        setNote,
        setOverrideEmail,
        setPhoneNumberCache,
        setPhoneNumberErrors,
        setPreviewCustomer,
        setSaveCustomer,
        setSelectedCustomer: saveSelectedCustomer,
        setShowBookingWizard,
        setShowDuplicateModal,
        setStep,
        setUnverifiedCustomer,
        setOverrideContact,
        helpers: {
          handledError,
          addRequestError,
          endSession,
          loadFromLocal,
          qualify,
          saveToLocal,
          setHandledError
        }
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};

export default SessionContextProvider;
